import React, { FC } from 'react';
import { Alert, FormGroup } from 'reactstrap';
import { RaffleWithUserDetails } from '../../shared/orders/raffle';
import { LoadingWrapper } from '../utils/LoadingWrapper';
import { ActionButton } from './ActionButton';

interface Props {
  readonly raffle?: Pick<RaffleWithUserDetails, 'id' | 'roleOverride'>;
}

export const RaffleContainer: FC<Props> = ({ children, raffle }) => {
  // If there's no raffle, or user is a volunteer and volunteerOverride is enabled
  if (!raffle || raffle.roleOverride) {
    return <>{children}</>;
  }

  return (
    <LoadingWrapper dataFetcher={async () => await api.getRaffleById(raffle.id)}>
      {(data, refresh) => (
        <RaffleState raffle={data} refresh={refresh}>
          {children}
        </RaffleState>
      )}
    </LoadingWrapper>
  );
};

interface RaffleStateProps {
  readonly raffle: RaffleWithUserDetails;
  readonly refresh: () => void;
}

const RaffleState: FC<RaffleStateProps> = ({ children, raffle, refresh }) => {
  const ticketStatus = raffle.ticket?.status;
  const hasEntry = ticketStatus === 'waiting';

  if (ticketStatus === 'winner' || ticketStatus === 'redeemed') {
    return (
      <>
        <Alert className="raffle-status-winner" color="success">
          You have won in this raffle and are eligible to purchase this product!
        </Alert>
        <br />
        {children}
      </>
    );
  }

  if (ticketStatus === 'rescinded') {
    return (
      <Alert className="raffle-status-rescinded" color="danger">
        Your ticket has been rescinded and you are ineligible to purchase this product at this time.
      </Alert>
    );
  }

  if (raffle.status !== 'open') {
    if (hasEntry) {
      return raffle.status === 'closed' ? (
        <Alert className="raffle-status-entered-pending-draw" color="info">
          <p>You're in the raffle for this product!</p>
          <p>The raffle is now closed to new entries and will be drawn soon.</p>
        </Alert>
      ) : (
        <Alert className="raffle-status-lost" color="secondary">
          <p>Unfortunately, you didn't win in this raffle.</p>
        </Alert>
      );
    }

    return (
      <Alert color="secondary">This raffled product is no longer accepting new entries.</Alert>
    );
  }

  return (
    <FormGroup>
      {hasEntry ? (
        <Alert className="raffle-status-entered" color="primary">
          <p>You're in the raffle for this product!</p>
          <p>Once the drawing takes place, if you win, you'll be able to purchase this product.</p>
        </Alert>
      ) : (
        <Alert className="raffle-status-not-entered" color="info">
          This is a raffled product which requires you to enter and win before it can be purchased.
        </Alert>
      )}
      <ActionButton
        action={`/api/raffles/${raffle.id}/participate`}
        block
        className={`raffle-button-${hasEntry ? 'leave' : 'enter'}`}
        color={hasEntry ? 'danger' : 'primary'}
        id={`raffle${raffle.id}`}
        method={hasEntry ? 'delete' : 'post'}
        onSuccess={refresh}
        outline={hasEntry}
        payload={hasEntry ? undefined : { join: true }}
      >
        {hasEntry ? 'Leave' : 'Enter'} Raffle
      </ActionButton>
    </FormGroup>
  );
};
